import React from "react";
import { FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import { ExportDataTypeInclusionSelector } from "./ExportDataTypeInclusionSelector";
import { EXPORT_INCLUSION_TYPES, type ExportFieldConfiguration } from "./types";
import { FieldConfigurationSelector } from "./FieldConfigurationSelector";
import { firmographicsExportFields } from "./exportConfigurationFields";
import { renderSpendCategoryFieldSelector } from "./hooks/renderSpendCategoryFieldSelector";
import { RadioOption } from "../RadioOptionsInput";

const DATA_TYPE = "Relative IT Spend";

interface Props {
  inclusion: EXPORT_INCLUSION_TYPES;
  setInclusion: (value: EXPORT_INCLUSION_TYPES) => void;
  fieldConfiguration: ExportFieldConfiguration;
  setFieldConfiguration: (cfg: ExportFieldConfiguration) => void;
  includeSMB: boolean;
  setIncludeSMB: (value: boolean) => void;
  enforceMandatoryFields?: boolean;
}

export const RelativeSpendExportOptions: React.FC<Props> = ({
  inclusion,
  setInclusion,
  fieldConfiguration,
  setFieldConfiguration,
  includeSMB,
  setIncludeSMB,
  enforceMandatoryFields,
}) => {
  const options: RadioOption[] = [
    {
      label: "Include all Relative Spend data",
      value: EXPORT_INCLUSION_TYPES.ALL,
    },
    {
      label: "Include only rows with Relative Spend data",
      value: EXPORT_INCLUSION_TYPES.FILTERS,
    },
    {
      label: "Do not include any Relative Spend data",
      value: EXPORT_INCLUSION_TYPES.NONE,
    },
  ];

  const isDisabled = inclusion === EXPORT_INCLUSION_TYPES.NONE;

  return (
    <Stack spacing={2} sx={{ width: "70%", paddingTop: "40px" }}>
      <Typography variant="h5" fontWeight={500} fontSize={16}>
        Relative IT Spend
      </Typography>
      <ExportDataTypeInclusionSelector
        dataType={DATA_TYPE}
        options={options}
        inclusion={inclusion}
        setInclusion={setInclusion}
      />
      <FieldConfigurationSelector
        fields={[]}
        firmographicsFields={firmographicsExportFields}
        value={fieldConfiguration}
        onChange={setFieldConfiguration}
        title="Relative Spend Categories"
        checkboxLabel="Export All Relative Spend Data Categories"
        renderFieldSelector={renderSpendCategoryFieldSelector}
        requiredFirmographicsField={
          enforceMandatoryFields
            ? {
                value: "company_id",
                note: "Note: Company ID will always be included in your export for proper company identification.",
              }
            : undefined
        }
        disabled={isDisabled}
      />
      <Typography variant="caption" color="text.secondary">
        Note: Spend Tier will be included in your export for proper spend tier
        identification.
      </Typography>
      <FormControlLabel
        control={
          <Switch
            checked={includeSMB}
            onChange={(e) => setIncludeSMB(e.target.checked)}
            disabled={isDisabled}
            data-testid="relative-spend-smb-toggle"
          />
        }
        label="Include SMB data"
      />
    </Stack>
  );
};
